import { useTranslation } from 'react-i18next'
import { useCallback, useEffect, useState } from 'react'
import { apiClient } from '@/lib/apiClient'

interface RegulatorySettings {
  residencyRegion: string | null
  gdprApplicable: boolean
  ccpaApplicable: boolean
  lgpdApplicable: boolean
  crossRegionAlerts: boolean
  retentionPolicyJson: string | null
}

const EMPTY: RegulatorySettings = {
  residencyRegion: null,
  gdprApplicable: false,
  ccpaApplicable: false,
  lgpdApplicable: false,
  crossRegionAlerts: false,
  retentionPolicyJson: null,
}

const REGIONS = ['US', 'EU', 'LATAM', 'APAC'] as const

export function RegulatoryPage() {
  const { t } = useTranslation()
  const [form, setForm] = useState<RegulatorySettings>(EMPTY)
  const [loadError, setLoadError] = useState(false)
  const [saving, setSaving] = useState(false)
  const [toast, setToast] = useState<string | null>(null)

  function flash(msg: string) {
    setToast(msg)
    setTimeout(() => setToast(null), 3000)
  }

  const load = useCallback(async () => {
    try {
      const res = await apiClient.get<RegulatorySettings>('/admin/regulatory')
      setForm(res.data)
    } catch {
      // 404 on first visit is expected — start from empty
      setLoadError(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const save = async () => {
    setSaving(true)
    try {
      await apiClient.put('/admin/regulatory', form)
      flash(t('regulatory.saved'))
    } catch {
      flash(t('regulatory.error_save'))
    } finally {
      setSaving(false)
    }
  }

  if (loadError) {
    return <p className="text-red-600 p-6">{t('regulatory.error_load')}</p>
  }

  const boolFields = [
    ['gdprApplicable', 'regulatory.gdpr_applicable'],
    ['ccpaApplicable', 'regulatory.ccpa_applicable'],
    ['lgpdApplicable', 'regulatory.lgpd_applicable'],
    ['crossRegionAlerts', 'regulatory.cross_region_alerts'],
  ] as const

  return (
    <div className="space-y-6 p-6 max-w-2xl">
      {toast && (
        <div className="fixed right-6 top-20 z-50 rounded-lg border bg-card px-4 py-2 text-sm shadow-lg">
          {toast}
        </div>
      )}

      <div>
        <h1 className="text-2xl font-semibold">{t('regulatory.title')}</h1>
        <p className="text-sm text-muted-foreground mt-1">{t('regulatory.subtitle')}</p>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-foreground mb-1">
            {t('regulatory.residency_region')}
          </label>
          <select
            className="border rounded px-3 py-2 text-sm w-full"
            value={form.residencyRegion ?? ''}
            onChange={e =>
              setForm(f => ({ ...f, residencyRegion: e.target.value || null }))
            }
          >
            <option value="" />
            {REGIONS.map(r => (
              <option key={r} value={r}>
                {t(`regulatory.region_${r}`, { defaultValue: r })}
              </option>
            ))}
          </select>
        </div>

        {boolFields.map(([field, labelKey]) => (
          <label key={field} className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={form[field]}
              onChange={e => setForm(f => ({ ...f, [field]: e.target.checked }))}
              className="rounded border-border"
            />
            {t(labelKey)}
          </label>
        ))}

        <div>
          <label className="block text-sm font-medium text-foreground mb-1">
            {t('regulatory.retention_policy')}
          </label>
          <textarea
            className="border rounded px-3 py-2 text-sm w-full font-mono"
            rows={4}
            placeholder={t('regulatory.retention_hint')}
            value={form.retentionPolicyJson ?? ''}
            onChange={e =>
              setForm(f => ({ ...f, retentionPolicyJson: e.target.value || null }))
            }
          />
        </div>
      </div>

      <button
        className="bg-blue-600 text-white px-4 py-2 rounded text-sm hover:bg-blue-700 disabled:opacity-50"
        disabled={saving}
        onClick={() => void save()}
      >
        {saving ? t('regulatory.saving') : t('regulatory.save')}
      </button>
    </div>
  )
}
